import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const tagFormSchema = z.object({
  name: z
    .string()
    .min(1, 'Tag name is required')
    .max(50, 'Tag name must be less than 50 characters')
    .trim(),
  color: z
    .string()
    .trim()
    .regex(
      /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/,
      'Invalid hex color code (e.g. #3B82F6)'
    )
    .default('#3B82F6'),
});

const TagForm = ({ tag, onSubmit, onCancel, isLoading = false }) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    watch,
  } = useForm({
    resolver: zodResolver(tagFormSchema),
    defaultValues: {
      name: '',
      color: '#3B82F6',
    },
  });

  const colorValue = watch('color');

  useEffect(() => {
    if (tag) {
      reset({
        name: tag.name || '',
        color: tag.color || '#3B82F6',
      });
    } else {
      reset();
    }
  }, [tag, reset]);

  return (
    <div className="bg-white p-6">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900">
          {tag ? 'Edit Tag' : 'Add New Tag'}
        </h2>
        <p className="text-sm text-gray-500">
          {tag ? 'Update tag details' : 'Enter a name for the new tag'}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label
            htmlFor="name"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Tag Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            {...register('name')}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
              errors.name ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="e.g. VIP, Customer, Lead"
            disabled={isLoading || isSubmitting}
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="color"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Tag Color
          </label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              id="color-picker"
              value={colorValue}
              onChange={e => setValue('color', e.target.value)}
              className="h-10 w-10 p-1 rounded-md border border-gray-300 cursor-pointer"
              disabled={isLoading || isSubmitting}
            />
            <input
              type="text"
              id="color"
              {...register('color')}
              className={`flex-1 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                errors.color ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="e.g. #3B82F6"
              disabled={isLoading || isSubmitting}
            />
          </div>
          {errors.color && (
            <p className="mt-1 text-sm text-red-600">{errors.color.message}</p>
          )}
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            disabled={isLoading || isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            disabled={isLoading || isSubmitting}
          >
            {isLoading || isSubmitting
              ? 'Saving...'
              : tag
                ? 'Update Tag'
                : 'Create Tag'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TagForm;
